/**
 * Recently viewed products — used by sections/custom-recently-viewed.liquid.
 *
 * Stores the last MAX_STORED product handles in localStorage. On a product
 * page it records the current handle, then loads up to `data-limit` earlier
 * products by requesting the same section for each one through the Section
 * Rendering API and copying the card out of its <template data-rv-card>.
 * Products that no longer load (unpublished, deleted, or on a template
 * without this section) are skipped and dropped from storage.
 */
const STORAGE_KEY = 'custom:recently-viewed';
const MAX_STORED = 12;

function readHandles() {
  try {
    const handles = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return Array.isArray(handles) ? handles : [];
  } catch {
    return [];
  }
}

function writeHandles(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_STORED)));
  } catch {
    // Storage unavailable (private mode, blocked): the section stays hidden.
  }
}

class CustomRecentlyViewed extends HTMLElement {
  connectedCallback() {
    const current = this.dataset.productHandle;
    const limit = Number(this.dataset.limit) || 4;
    const previous = readHandles().filter((handle) => handle !== current);

    if (current) writeHandles([current, ...previous]);
    if (previous.length > 0) this.#render(previous, limit);
  }

  async #fetchCard(handle) {
    const root = window.Shopify?.routes?.root ?? '/';
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.dataset.sectionId)}`;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      return html.querySelector('template[data-rv-card]')?.content ?? null;
    } catch {
      return null;
    }
  }

  async #render(handles, limit) {
    const list = this.querySelector('[data-rv-list]');
    if (!list) return;

    // Ask for a few extra in case some no longer load.
    const candidates = handles.slice(0, limit + 2);
    const cards = await Promise.all(candidates.map((handle) => this.#fetchCard(handle)));

    const missing = candidates.filter((_, index) => !cards[index]);
    if (missing.length > 0) {
      writeHandles(readHandles().filter((handle) => !missing.includes(handle)));
    }

    const found = cards.filter(Boolean).slice(0, limit);
    if (found.length === 0) return;

    list.replaceChildren(...found);
    this.hidden = false;
  }
}

if (!customElements.get('custom-recently-viewed')) {
  customElements.define('custom-recently-viewed', CustomRecentlyViewed);
}
